import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  DestroyRef,
  inject,
  LOCALE_ID,
  OnInit,
  signal,
} from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { DatePipe, formatDate } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { forkJoin } from 'rxjs';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';

import { DfService } from '../../../../core/df/df.service';
import {
  DIRECT_APPLICATIONS_PAUSED,
  preApplicationLink,
} from '../../../../core/applications/direct-applications';
import { DfFacility } from '../../../../core/df/df.types';
import { EntityService } from '../../../../core/entity/entity.service';
import { EntityDetail } from '../../../../core/entity/entity.types';
import { IconComponent } from '../../../../shared/icon/icon';
import { StatusChipComponent } from '../../../../shared/status-chip/status-chip';
import { ZarPipe } from '../../../../shared/pipes/zar.pipe';
import { DashPipe } from '../../../../shared/pipes/dash.pipe';
import { AgoPipe } from '../../../../shared/pipes/ago.pipe';

@Component({
  selector: 'app-df-list',
  imports: [
    MatCardModule,
    MatButtonModule,
    MatTableModule,
    MatTooltipModule,
    RouterLink,
    DatePipe,
    IconComponent,
    StatusChipComponent,
    ZarPipe,
    DashPipe,
    AgoPipe,
  ],
  templateUrl: './df-list.html',
  styleUrl: './df-list.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DfListComponent implements OnInit {
  private readonly dfService = inject(DfService);
  private readonly entityService = inject(EntityService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);
  private readonly locale = inject(LOCALE_ID);

  protected readonly entityId = this.route.snapshot.paramMap.get('id') ?? '';
  protected readonly entity = signal<EntityDetail | null>(null);
  protected readonly facilities = signal<DfFacility[]>([]);
  protected readonly isLoading = signal(true);
  protected readonly loadError = signal(false);

  protected readonly columns = [
    'entity',
    'requested_limit',
    'status',
    'stage',
    'started',
    'actions',
  ];

  /** Paused 2026-10-22 (FLOW-394): direct applications go through the pre-application. */
  protected readonly directApplicationsPaused = DIRECT_APPLICATIONS_PAUSED;

  /** The applicant's open, resumable draft facility for this business, if any. */
  protected readonly openDraft = computed(() =>
    this.facilities().find((f) => f.application_stage === 'DRAFT'),
  );

  ngOnInit(): void {
    this.load();
  }

  /**
   * Hover summary for the Started cell — the facility's full timeline in one
   * place: when it was started, last edited, and submitted. Keeps the table to a
   * single date column while still surfacing the other timestamps on demand.
   */
  protected activityTooltip(f: DfFacility): string {
    const fmt = (value: string | null): string =>
      value ? formatDate(value, 'medium', this.locale) : 'Not yet submitted';
    return [
      `Started: ${formatDate(f.created_at, 'medium', this.locale)}`,
      `Last worked on: ${formatDate(f.updated_at, 'medium', this.locale)}`,
      `Submitted: ${fmt(f.submitted_at)}`,
    ].join('\n');
  }

  /**
   * Start a new facility — but funnel back to an existing open draft rather
   * than spawning a second one. Prevents orphan drafts accumulating when a user
   * abandons the wizard and later clicks "New" again.
   */
  protected startOrResume(): void {
    const draft = this.openDraft();
    void this.router.navigate(
      draft
        ? ['/entity', this.entityId, 'df', draft.short_id]
        : this.newFacilityLink(),
    );
  }

  /** Paused 2026-10-22 (FLOW-394): direct applications go through the pre-application. */
  private newFacilityLink(): string[] {
    return this.directApplicationsPaused
      ? preApplicationLink(this.entityId)
      : ['/entity', this.entityId, 'df', 'new'];
  }

  protected reload(): void {
    this.loadError.set(false);
    this.isLoading.set(true);
    this.load();
  }

  private load(): void {
    forkJoin({
      entity: this.entityService.getEntity(this.entityId),
      facilities: this.dfService.listFacilitiesForEntity(this.entityId),
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ({ entity, facilities }) => {
          this.entity.set(entity);
          this.facilities.set(facilities.facilities);

          // Smart entry: a business with no Debtors Finance facility has nothing
          // to list, so send them straight to the application. `replaceUrl` keeps
          // Back returning to the dashboard rather than flashing this empty list.
          if (facilities.facilities.length === 0) {
            void this.router.navigate(this.newFacilityLink(), {
              replaceUrl: true,
            });
            return;
          }

          this.isLoading.set(false);
          this.cdr.markForCheck();
        },
        error: () => {
          this.isLoading.set(false);
          this.loadError.set(true);
          this.cdr.markForCheck();
        },
      });
  }
}
